/**
 * Per-card coordination context. Provides the swipe-coordination hooks that
 * `Swipable`-using cards (e.g. `YesNoQuestion`) need to inform the deck about
 * mid-swipe state. The id is baked in by `DeckCard`, so consumers never have
 * to thread it.
 */
import { createContext, use } from "react";

import type { ViewMode } from "@/web/routes/_protected/smb/-dashboard/-deckAnimate";

export type DeckCardCoordination = {
  beginSwipePending: (direction: "left" | "right") => void;
  resolveSwipe: (outcome: "committed" | "rejected") => void;
  /**
   * Skip this card, or `null` when skipping isn't permitted (the action opted
   * out via `canSkip`, or it's the sole remaining live card). `ActionCard`
   * reads this to wire the universal swipe-left-to-skip gesture, which is why
   * it lives on the context rather than being threaded through every card
   * type's props.
   */
  skip: (() => void) | null;
  /**
   * Current deck layout. Swipe-to-skip is a stack-mode affordance only — in
   * grid every card is already visible, so reordering one to the back is a
   * pointless shuffle, and many simultaneously-draggable cards in a
   * vertically-scrolling grid is a lot of accidental-skip surface.
   */
  viewMode: ViewMode;
};

export const DeckCardCoordinationContext = createContext<DeckCardCoordination | null>(null);

/**
 * Read swipe-coordination hooks for the surrounding card. Returns `null` when
 * the card is rendered outside a deck (e.g. on the completed-list grid),
 * which lets cards work in both contexts without coupling.
 */
export function useDeckCardCoordination(): DeckCardCoordination | null {
  return use(DeckCardCoordinationContext);
}
